"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Code, Copy, Check } from "lucide-react";
import { generateCodeExample } from "@/lib/playground-utils";
import { Capability } from "./prompt-types";
import { PromptOptions } from "@/components/playground/prompt-caption";

// Language icons as SVG components - using the same icons as the docs
const PythonIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M14.25.18l.9.2.73.26.59.3.45.32.34.34.25.34.16.33.1.3.04.26.02.2-.01.13V8.5l-.05.63-.13.55-.21.46-.26.38-.3.31-.33.25-.35.19-.35.14-.33.1-.3.07-.26.04-.21.02H8.77l-.69.05-.59.14-.5.22-.41.27-.33.32-.27.35-.2.36-.15.37-.1.35-.07.32-.04.27-.02.21v3.06H3.17l-.21-.03-.28-.07-.32-.12-.35-.18-.36-.26-.36-.36-.35-.46-.32-.59-.28-.73-.21-.88-.14-1.05-.05-1.23.06-1.22.16-1.04.24-.87.32-.71.36-.57.4-.44.42-.33.42-.24.4-.16.36-.1.32-.05.26-.02.21-.01h5.84l.69-.05.59-.14.5-.21.41-.28.33-.32.27-.35.2-.36.15-.36.1-.35.07-.32.04-.28.02-.21V6.07h2.09l.14.01zm-6.47 14.25c-.2 0-.37.09-.5.27-.13.18-.2.39-.2.61 0 .22.07.43.2.61.13.18.3.27.5.27.2 0 .37-.09.5-.27.13-.18.2-.39.2-.61 0-.22-.07-.43-.2-.61-.13-.18-.3-.27-.5-.27z"/>
  </svg>
);

const JavaScriptIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M0 0h24v24H0V0zm22.034 18.276c-.175-1.095-.888-2.015-3.003-2.873-.736-.345-1.554-.585-1.797-1.14-.091-.33-.105-.51-.046-.705.15-.646.915-.84 1.515-.66.39.12.75.42.976.9 1.034-.676 1.034-.676 1.755-1.125-.27-.42-.404-.601-.586-.78-.63-.705-1.469-1.065-2.834-1.034l-.705.089c-.676.165-1.32.525-1.71 1.005-1.14 1.291-.811 3.541.569 4.471 1.365 1.02 3.361 1.244 3.616 2.205.24 1.17-.87 1.545-1.966 1.41-.811-.18-1.26-.586-1.755-1.336l-1.83 1.051c.21.48.45.689.81 1.109 1.74 1.756 6.09 1.666 6.871-1.004.029-.09.24-.705.074-1.65l.046.067zm-8.983-7.245h-2.248c0 1.938-.009 3.864-.009 5.805 0 1.232.063 2.363-.138 2.711-.33.689-1.18.601-1.566.48-.396-.196-.597-.466-.83-.855-.063-.105-.11-.196-.127-.196l-1.825 1.125c.305.63.75 1.172 1.324 1.517.855.51 2.004.675 3.207.405.783-.226 1.458-.691 1.811-1.411.51-.93.402-2.07.397-3.346.012-2.054 0-4.109 0-6.179l.004-.056z"/>
  </svg>
);

const BashIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M21.038 4.9l-7.577-4.498a2.036 2.036 0 0 0-2.032 0L3.85 4.9a2.036 2.036 0 0 0-1.016 1.765v9.67a2.036 2.036 0 0 0 1.016 1.765l7.579 4.498a2.036 2.036 0 0 0 2.032 0l7.577-4.498a2.036 2.036 0 0 0 1.016-1.765v-9.67A2.036 2.036 0 0 0 21.038 4.9zM12 19.15L5.346 15.5V8.5L12 4.85 18.654 8.5v7L12 19.15z"/>
    <path d="M8.5 10.5l2 1.5-2 1.5v1l3-2.25v-.5L8.5 9.5v1zm4.5 2.5h2v1h-2v-1z"/>
  </svg>
);

interface CodeExampleProps {
  capability: Capability;
  prompt: string;
  imageFile: File;
  options?: PromptOptions;
}

export default function CodeExample({
  capability,
  prompt,
  imageFile,
  options,
}: CodeExampleProps) {
  const [copied, setCopied] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const codeExamples = generateCodeExample(capability, prompt, imageFile, options);

  const copyToClipboard = async (text: string, language: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(language);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  if (!isOpen) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(true)}
        className="mt-2"
      >
        <Code className="w-4 h-4 mr-2" />
        Show Code
      </Button>
    );
  }

  return (
    <Card className="mt-4">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Code Example</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsOpen(false)}
          >
            Close
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="python" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="python" className="flex items-center gap-2">
              <PythonIcon className="w-4 h-4" />
              Python
            </TabsTrigger>
            <TabsTrigger value="javascript" className="flex items-center gap-2">
              <JavaScriptIcon className="w-4 h-4" />
              JavaScript
            </TabsTrigger>
            <TabsTrigger value="curl" className="flex items-center gap-2">
              <BashIcon className="w-4 h-4" />
              cURL
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="python" className="mt-4">
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                className="absolute top-2 right-2"
                onClick={() => copyToClipboard(codeExamples.python, "python")}
              >
                {copied === "python" ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
              <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto">
                <code>{codeExamples.python}</code>
              </pre>
            </div>
          </TabsContent>
          
          <TabsContent value="javascript" className="mt-4">
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                className="absolute top-2 right-2"
                onClick={() => copyToClipboard(codeExamples.javascript, "javascript")}
              >
                {copied === "javascript" ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
              <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto">
                <code>{codeExamples.javascript}</code>
              </pre>
            </div>
          </TabsContent>
          
          <TabsContent value="curl" className="mt-4">
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                className="absolute top-2 right-2"
                onClick={() => copyToClipboard(codeExamples.curl, "curl")}
              >
                {copied === "curl" ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
              <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto">
                <code>{codeExamples.curl}</code>
              </pre>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
} 
